/* ---------------------------------------------------------------------------
   SPARTAN — kit line product preview

   Every equipment line inside a package card is a link to that product. On a
   fine pointer, hovering (or keyboard-focusing) a line raises a small preview
   card: photo, brand and the spec note we attached to that line.

   Design decisions worth keeping:

   • ONE card, appended to <body>, position:fixed. The obvious approach is a
     popover per <li>, but package cards sit in a CSS grid and a sibling card
     will overlap an absolutely-positioned child. Fixed positioning off the body
     escapes every stacking and clipping context on the page.

   • The card is pointer-events:none. It is a preview, not a menu — nothing in
     it needs clicking, because the line itself is already the link. This also
     removes the classic hover-trap bug where moving toward the card crosses
     dead space and dismisses it.

   • Coarse pointers get nothing. No hover on touch, and a tap-to-preview
     interaction would just delay the navigation the user actually wanted.
     The link works; that is the whole mobile story.

   • Styles are injected from here rather than added to spartan-platinum.css.
     Everything is prefixed .sp-kp- and used nowhere else, so deleting this file
     removes the feature completely with no orphaned CSS.

   The two rules that beat the theme use `.sp-scope a.sp-kp-link` (0,2,1)
   deliberately: `.sp-scope a{color:inherit}` is (0,1,1) and would otherwise win.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  var SEL = '[data-sp-kp]';
  var OPEN_DELAY = 140;
  var CLOSE_DELAY = 90;
  var GAP = 12;

  if (!window.matchMedia) return;
  var finePointer = window.matchMedia('(hover:hover) and (pointer:fine)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion:reduce)');

  var card = null;
  var openTimer = null;
  var closeTimer = null;
  var current = null;

  function injectStyles() {
    if (document.getElementById('sp-kp-styles')) return;
    var css = [
      '.sp-scope a.sp-kp-link{color:inherit;text-decoration:none;',
      'border-bottom:1px solid rgba(255,255,255,.14);transition:color .15s ease,border-color .15s ease}',
      '.sp-scope a.sp-kp-link:hover,.sp-scope a.sp-kp-link:focus-visible{color:#fff;border-bottom-color:rgba(255,255,255,.5)}',
      '.sp-scope a.sp-kp-link:focus-visible{outline:2px solid #c8c8d0;outline-offset:3px;border-radius:2px}',

      '.sp-kp{position:fixed;z-index:2147483000;top:0;left:0;width:304px;pointer-events:none;',
      'opacity:0;transform:translateY(6px) scale(.985);transform-origin:center left;',
      'transition:opacity .18s cubic-bezier(.2,.7,.3,1),transform .18s cubic-bezier(.2,.7,.3,1);',
      'background:#111114;border:1px solid #33333b;border-radius:12px;',
      'box-shadow:0 2px 6px rgba(0,0,0,.35),0 28px 64px rgba(0,0,0,.66);overflow:hidden;',
      '-webkit-font-smoothing:antialiased;',
      "font-family:'Montserrat',system-ui,-apple-system,sans-serif}",
      '.sp-kp[hidden]{display:none}',
      '.sp-kp.is-on{opacity:1;transform:translateY(0) scale(1)}',

      /* Platinum hairline across the top — the one nod to the brand gradient. */
      '.sp-kp::before{content:"";position:absolute;top:0;left:0;right:0;height:2px;z-index:2;',
      'background:linear-gradient(105deg,#8b8b92 0%,#e9e9ee 28%,#fff 50%,#c2c2c9 72%,#8b8b92 100%)}',

      /* Media plate. Flat white, no gradient and no divider rule: the supplier
         shots are all photographed on white, so any tint or edge here reads as a
         grey box drawn around the product. Matching the photo's own background
         lets the machine sit in the card with no visible plate at all.

         The image is absolutely positioned inside a fixed box so no theme-level
         `img{}` rule can stretch it into the text below. */
      '.sp-kp-media{position:relative;height:172px;background:#fff;display:block}',
      '.sp-kp .sp-kp-media img{position:absolute;top:0;left:0;width:100%;height:100%;',
      'object-fit:contain;padding:16px;box-sizing:border-box;display:block;margin:0;max-width:none}',
      '.sp-kp-media.is-empty{height:0}',
      '.sp-kp-media.is-empty img{display:none}',

      '.sp-kp-body{padding:15px 16px 16px}',
      '.sp-kp-vendor{font-size:9.5px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;',
      'color:#7e7e88;margin:0 0 6px;line-height:1}',
      ".sp-kp-title{font-family:'Roboto',system-ui,sans-serif;font-weight:700;font-size:16.5px;",
      'line-height:1.22;color:#f5f5f7;margin:0;letter-spacing:.1px}',
      '.sp-kp-note{font-size:11.5px;line-height:1.55;color:#9e9ea8;margin:12px 0 0;',
      'padding-top:11px;border-top:1px solid #26262d}',
      '.sp-kp-go{font-size:10px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;',
      'color:#6f6f79;margin:13px 0 0;display:flex;align-items:center;gap:6px;line-height:1}',
      '.sp-kp-go span{transform:translateY(-.5px)}',

      '@media(prefers-reduced-motion:reduce){.sp-kp{transition:opacity .01ms linear;transform:none}',
      '.sp-kp.is-on{transform:none}}'
    ].join('');
    var el = document.createElement('style');
    el.id = 'sp-kp-styles';
    el.textContent = css;
    document.head.appendChild(el);
  }

  function build() {
    if (card) return card;
    card = document.createElement('div');
    card.className = 'sp-kp';
    card.setAttribute('role', 'tooltip');
    card.hidden = true;
    card.innerHTML =
      '<div class="sp-kp-media"><img alt="" decoding="async"></div>' +
      '<div class="sp-kp-body">' +
        '<p class="sp-kp-vendor"></p>' +
        '<p class="sp-kp-title"></p>' +
        '<p class="sp-kp-note" hidden></p>' +
        '<p class="sp-kp-go">View product <span aria-hidden="true">&rarr;</span></p>' +
      '</div>';
    document.body.appendChild(card);
    return card;
  }

  function fill(link) {
    var c = build();
    var media = c.querySelector('.sp-kp-media');
    var img = c.querySelector('.sp-kp-media img');
    var src = link.getAttribute('data-kp-img');

    if (src) {
      media.classList.remove('is-empty');
      img.src = src;
      img.alt = '';
    } else {
      // No photo is better than a broken frame. Collapse the plate entirely.
      media.classList.add('is-empty');
      img.removeAttribute('src');
    }

    c.querySelector('.sp-kp-vendor').textContent = link.getAttribute('data-kp-vendor') || '';
    c.querySelector('.sp-kp-title').textContent = link.getAttribute('data-kp-title') || '';

    var note = link.getAttribute('data-kp-note');
    var noteEl = c.querySelector('.sp-kp-note');
    noteEl.textContent = note || '';
    noteEl.hidden = !note;
  }

  function place(link) {
    var c = build();
    var cw = c.offsetWidth;
    var ch = c.offsetHeight;
    var vw = document.documentElement.clientWidth;
    var vh = document.documentElement.clientHeight;

    // Anchor horizontally to the whole package card, not the line.
    //
    // Anchoring to the line put the card on top of the very list the buyer was
    // reading — it covered the neighbouring package. Sitting the preview beside
    // the card keeps every equipment list legible while it is open.
    var kit = link.closest('.sp-kit') || link;
    var kr = kit.getBoundingClientRect();
    var lr = link.getBoundingClientRect();

    var left = kr.right + GAP;                        // preferred: right of the card
    if (left + cw > vw - 10) left = kr.left - cw - GAP; // flip: left of the card
    if (left < 10) {
      // Neither side fits (narrow window). Fall back to the line and clamp.
      left = Math.min(Math.max(10, lr.right + GAP), vw - cw - 10);
    }

    // Vertically track the hovered line so the connection stays obvious.
    var top = lr.top + lr.height / 2 - ch / 2;
    if (top < 10) top = 10;
    if (top + ch > vh - 10) top = Math.max(10, vh - ch - 10);

    c.style.left = Math.round(left) + 'px';
    c.style.top = Math.round(top) + 'px';
    c.style.transformOrigin = (left > kr.left ? 'center left' : 'center right');
  }

  function open(link) {
    current = link;
    fill(link);
    var c = build();
    c.hidden = false;
    // Measure after the browser has the content, then reveal.
    requestAnimationFrame(function () {
      if (current !== link) return;
      place(link);
      requestAnimationFrame(function () {
        if (current !== link) return;
        c.classList.add('is-on');
      });
    });
  }

  function close() {
    current = null;
    if (!card) return;
    card.classList.remove('is-on');
    var c = card;
    var done = function () { if (!c.classList.contains('is-on')) c.hidden = true; };
    if (reduceMotion.matches) done();
    else setTimeout(done, 180);
  }

  function scheduleOpen(link) {
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    openTimer = setTimeout(function () { open(link); }, OPEN_DELAY);
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, CLOSE_DELAY);
  }

  function bind() {
    if (!finePointer.matches) return;
    injectStyles();

    document.addEventListener('mouseover', function (e) {
      var link = e.target.closest && e.target.closest(SEL);
      if (link) scheduleOpen(link);
    });
    document.addEventListener('mouseout', function (e) {
      var link = e.target.closest && e.target.closest(SEL);
      if (link) scheduleClose();
    });

    // Keyboard parity: focus opens, blur closes, Escape dismisses.
    document.addEventListener('focusin', function (e) {
      var link = e.target.closest && e.target.closest(SEL);
      if (link) scheduleOpen(link);
      else if (current) scheduleClose();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && current) { clearTimeout(openTimer); close(); }
    });

    // Any viewport change invalidates the anchor position — cheaper and less
    // jittery to dismiss than to keep recomputing.
    window.addEventListener('scroll', function () {
      if (current) { clearTimeout(openTimer); close(); }
    }, { passive: true });
    window.addEventListener('resize', function () {
      if (current) { clearTimeout(openTimer); close(); }
    });
  }

  // Only wire up the styles/behaviour once we know a fine pointer is present.
  // matchMedia can flip when a mouse is attached to a tablet mid-session.
  if (finePointer.matches) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind);
    } else {
      bind();
    }
  } else if (finePointer.addEventListener) {
    finePointer.addEventListener('change', function once(ev) {
      if (ev.matches) { finePointer.removeEventListener('change', once); bind(); }
    });
  }
})();
